function toTitleCase(s: string) {
  return s
    .trim()
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/(^|\s)\S/g, (m) => m.toUpperCase());
}

function looksLikeAutoId(id: string) {
  const v = id.trim();
  // GrapesJS often generates short random-ish ids like "lua0ki", "lqg9zr".
  return /^[a-z0-9]{5,8}$/i.test(v);
}

function safeStr(v: unknown) {
  return typeof v === 'string' ? v : '';
}

function getAttrs(cmp: any): Record<string, string> {
  const raw = cmp?.getAttributes?.() ?? cmp?.get?.('attributes') ?? {};
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (typeof v === 'string') out[k] = v;
  }
  return out;
}

function getClassTokens(attrs: Record<string, string>) {
  return (attrs.class ?? '')
    .split(/\s+/g)
    .map((t) => t.trim())
    .filter(Boolean);
}

function hasAll(tokens: string[], need: string[]) {
  const set = new Set(tokens);
  return need.every((t) => set.has(t));
}

function classify(cmp: any): string | null {
  const tag = safeStr(cmp?.get?.('tagName')).toLowerCase();
  const type = safeStr(cmp?.get?.('type')).toLowerCase();
  const attrs = getAttrs(cmp);
  const id = safeStr(attrs.id).toLowerCase();
  const role = safeStr(attrs.role).toLowerCase();
  const dataSection = safeStr(attrs['data-section']);
  const ariaLabel = safeStr(attrs['aria-label']);
  const placeholder = safeStr(attrs.placeholder);
  const href = safeStr(attrs.href);
  const classTokens = getClassTokens(attrs);

  if (dataSection) {
    return `Section: ${toTitleCase(dataSection)}`;
  }

  if (tag === 'header') return 'Header';
  if (tag === 'main') return 'Main';
  if (tag === 'nav') {
    if (hasAll(classTokens, ['fixed', 'bottom-0', 'w-full'])) return 'Tab Bar';
    return 'Nav';
  }
  if (tag === 'section') return 'Section';

  if (id.includes('tab') && hasAll(classTokens, ['fixed', 'bottom-0', 'w-full'])) return 'Tab Bar';
  if (id.includes('sheet') && (hasAll(classTokens, ['fixed', 'bottom-0']) || classTokens.includes('bottom-0'))) return 'Bottom Sheet';
  if (id.includes('modal') || id.includes('dialog')) return 'Modal';
  if (id.includes('detail')) return 'Detail';
  if (id.includes('filter')) return 'Filter';
  if (id.includes('search')) return 'Search';
  if (id.includes('profile')) return 'Profile';

  if (tag === 'button') {
    if (ariaLabel) return `Button: ${ariaLabel}`;
    if (id) return `Button: ${toTitleCase(id)}`;
    return 'Button';
  }

  if (tag === 'a') {
    if (ariaLabel) return `Link: ${ariaLabel}`;
    if (href) return 'Link';
    return 'Link';
  }

  if (tag === 'input' || type === 'input') {
    if (ariaLabel) return `Input: ${ariaLabel}`;
    if (placeholder) return `Input: ${placeholder}`;
    if (id) return `Input: ${toTitleCase(id)}`;
    return 'Input';
  }

  if (tag === 'img') {
    const alt = safeStr(attrs.alt);
    return alt ? `Image: ${alt}` : 'Image';
  }

  if ((role === 'button' || role === 'link') && id) return toTitleCase(id);

  // Heuristics for common app blocks (avoid going too deep / too smart).
  if (hasAll(classTokens, ['fixed', 'top-0', 'w-full'])) return 'Top Bar';

  if (id && !looksLikeAutoId(id)) return toTitleCase(id);

  return null;
}

function isGenericName(name: string) {
  const n = name.trim().toLowerCase();
  return !n || n === 'div' || n === 'section' || n === 'body' || n === 'wrapper';
}

export function autoNameLayers(editor: any) {
  const wrapper = editor?.getWrapper?.();
  if (!wrapper) return;

  const visit = (cmp: any) => {
    const existing = safeStr(cmp?.get?.('name'));
    const next = classify(cmp);
    if (next && (isGenericName(existing) || !existing)) {
      try {
        cmp.set?.('name', next);
      } catch {
        // ignore
      }
    }

    const children = cmp?.components?.();
    if (children && typeof children.forEach === 'function') {
      children.forEach((ch: any) => visit(ch));
    }
  };

  visit(wrapper);
}
